import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { api, API, formatValue } from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import FormatBadge from "@/components/FormatBadge";
import DmeTag from "@/components/DmeTag";
import { Loader2, Trophy, Share2 } from "lucide-react";

function contenderTier(rank, total) {
  if (!total) return null;
  if (rank <= Math.round(total / 3)) return { label: "Contender", color: "#F5B301" };
  if (rank <= Math.round((2 * total) / 3)) return { label: "On the Bubble", color: "#7DD3FC" };
  return { label: "Rebuilding", color: "#F87171" };
}

export default function PublicRankings() {
  const { shareId } = useParams();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy — copy the URL from your address bar."); }
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/public/rankings/${shareId}`)
      .then(r => setSnap(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [shareId]);

  const total = snap?.rankings?.length || 0;
  const title = snap ? `${snap.league_name} — Dynasty Power Rankings` : "Dynasty Power Rankings";

  return (
    <PublicShell>
      <Helmet>
        <title>{`${title} | Dynasty Metrics`}</title>
        <meta name="description" content="Live dynasty power rankings ranked by Dynasty Metrics proprietary DME value — starters, bench and draft capital across the league." />
        <meta property="og:title" content={title} />
        <meta property="og:image" content={`${API}/og/rankings/${shareId}.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`${API}/og/rankings/${shareId}.png`} />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {loading && <div className="card-bordered p-12 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>}

      {!loading && notFound && (
        <div className="card-bordered p-12 text-center" data-testid="public-rankings-notfound">
          <div className="font-display text-xl font-bold mb-2">Snapshot not found</div>
          <p className="text-sm text-[var(--text-2)] mb-5">This shared link may have expired or never existed.</p>
          <Link to="/" className="btn-primary text-sm inline-block">Explore Dynasty Metrics</Link>
        </div>
      )}

      {!loading && snap && (
        <div className="space-y-6 fade-up" data-testid="public-rankings">
          <header>
            <div className="text-[var(--accent)] text-xs tracking-widest mb-1 font-mono">// SHARED POWER RANKINGS</div>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="font-display text-4xl font-black uppercase tracking-tight" data-testid="public-rankings-heading">{snap.league_name}</h1>
                <div className="text-sm text-[var(--text-2)] mt-1 flex items-center gap-2">
                  Ranked by <span className="inline-flex items-center gap-1 text-[var(--accent)]">Dynasty Metrics <DmeTag /></span>
                  <FormatBadge format={snap.league_format || snap.format} />
                </div>
              </div>
              <button onClick={copyLink} data-testid="public-rankings-share-btn"
                className="btn-secondary text-sm inline-flex items-center gap-1.5 shrink-0"
                title="Copy this rankings link">
                <Share2 className="w-4 h-4" /> Copy link
              </button>
            </div>
          </header>

          <div className="card-bordered overflow-hidden">
            <table className="w-full text-sm tabular" data-testid="public-rankings-table">
              <thead className="text-[var(--text-3)] text-xs uppercase tracking-wider">
                <tr className="border-b border-[var(--border)]">
                  <th className="text-left p-3 w-12">#</th>
                  <th className="text-left p-3">Owner</th>
                  <th className="text-right p-3 hidden sm:table-cell">Starters</th>
                  <th className="text-right p-3 hidden sm:table-cell">Bench</th>
                  <th className="text-right p-3 hidden sm:table-cell">Picks</th>
                  <th className="text-right p-3"><span className="inline-flex items-center gap-1.5 justify-end">Total <DmeTag /></span></th>
                </tr>
              </thead>
              <tbody>
                {snap.rankings.map((r) => {
                  const t = contenderTier(r.rank, total);
                  return (
                    <tr key={r.rank} className="border-b border-[var(--border)]" data-testid={`public-pr-row-${r.rank}`}>
                      <td className="p-3 font-semibold">{r.rank === 1 ? <Trophy className="w-4 h-4 inline text-[var(--accent)]" /> : r.rank}</td>
                      <td className="p-3 font-medium">
                        <div className="flex items-center gap-2">
                          <span>{r.owner_name}</span>
                          {t && (
                            <span className="inline-flex items-center text-[9px] font-mono font-bold uppercase tracking-wider rounded px-1.5 py-0.5 leading-none"
                              style={{ color: t.color, border: `1px solid ${t.color}55`, background: `${t.color}14` }}>
                              {t.label}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-right hidden sm:table-cell">{formatValue(r.starter_value)}</td>
                      <td className="p-3 text-right text-[var(--text-2)] hidden sm:table-cell">{formatValue(r.bench_value)}</td>
                      <td className="p-3 text-right text-[var(--text-2)] hidden sm:table-cell">{formatValue(r.picks_value)}</td>
                      <td className="p-3 text-right font-semibold text-[var(--accent)]">{formatValue(r.total_value)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="card-bordered p-6 text-center" data-testid="public-rankings-cta">
            <div className="font-display text-xl font-bold mb-2">Want this for your leagues?</div>
            <p className="text-sm text-[var(--text-2)] max-w-md mx-auto mb-4">
              Sync your Sleeper leagues to get live power rankings, multi-league portfolio analytics, AI trade pitches and a real-time trade tape — free for 7 days.
            </p>
            <Link to="/login?signup=1" className="btn-primary text-sm inline-block" data-testid="public-rankings-cta-btn">Start free — sync my leagues</Link>
          </div>
        </div>
      )}
    </PublicShell>
  );
}
